import {
  Id,
  Identifiable,
  List,
  Milestone,
  project,
  ProjectStatus,
  ResourceTypes,
  Role,
  Tag,
  Task,
  User,
  Zone,
  Event,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";
import { Linker } from "../linkers/types";
import { mapError } from "../utils/mapError";

type Activity = Identifiable<List> & {
  parentActivity: Identifiable<Activity> | null;
};

type Response = {
  uuid: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  createdDate: string;
  completionDate: string;
  percentComplete: number;
  status: {
    uuid: string;
    percentComplete?: number;
    label: string;
    color: string;
    isBasic: boolean;
    keyRank: number;
  };
  tags: string[];
  activityKeyRank: number;
  statusKeyRank: number;
  duration: number;
  allocations: [
    {
      uuid: string;
      allocatedHours: number;
      percentComplete: number;
      estimatedAt: number;
      completedAt: string;
      resource: {
        uuid: string;
        type: ResourceTypes;
        role?: Identifiable<Role>;
        affiliation: "INTERNAL" | "EXTERNAL" | null;
        name: string;
        profession: string;
        description: string;
        integratedAt: string | null;
        avatarUrl: string;
        createdDate: string;
        invitationAccepted: boolean;
        user: Identifiable<User> | null;
        tags: Tag[];
        deactivated: boolean;
      };
    },
  ];
  activity: {
    uuid: string;
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    createdDate: string;
    parentActivity: Identifiable<Activity>;
    subLists: Identifiable<List>[];
    tasks: Identifiable<Task>[];
    events: Identifiable<Event>[];
    milestones: Identifiable<Milestone>[];
    tags: Tag[];
    activityKeyRank: number;
    statusKeyRank: number;
    minStartDate: string;
    maxStartDate: string;
    minEndDate: string;
    maxEndDate: string;
  };
  project: {
    uuid: string;
    name: string;
    description: string;
    zone: Identifiable<Zone>;
    status: Identifiable<ProjectStatus>;
    startDate: string;
    endDate: string;
    attributes: { [key: string]: string };
  };
  minStartDate: string;
  maxEndDate: string;
  lastModified: string;
};

function renameActivities(
  activity: Identifiable<Activity> | null,
): Identifiable<List> | undefined {
  return !activity
    ? undefined
    : {
        ...activity,
        parentList: renameActivities(activity.parentActivity),
      };
}

function getTask(httpClient: HttpClient, linker: Linker) {
  return (taskId: Id) =>
    httpClient
      .sendHttpRequest<Response>({
        endpoint: `/tasks/${taskId}?projection=taskDetailsProjection`,
        method: HttpMethods.GET,
      })
      .then((task) => ({
        ...task,
        list: renameActivities(task.activity) as Identifiable<List>,
        status: { ...task.status, basic: task.status.isBasic },
        project: {
          ...project,
          ...task.project,
          // @ts-ignore
          isTemplate: project.isTemplate || project.template,
        },
        tags: task.tags.map((name) => ({ name })),
      }))
      .catch((e) =>
        Promise.reject(
          mapError(e, {
            notFound: "notFound",
          }),
        ),
      );
}

export { getTask };
